import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import { Loader2, X, Trash2, Plus, CheckCircle2, ChevronDown, ChevronRight, Ruler, History, Mail, Camera } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import RoofSnapshotConfirm from '@/pages/workflow/renderers/RoofSnapshotConfirm';
import AnnotateRoofDiagram from '@/pages/workflow/renderers/AnnotateRoofDiagram';

const OUTCOME = { done: ['Done', 'bg-emerald-100 text-emerald-800'], quoted: ['Quoted, not done', 'bg-amber-100 text-amber-800'], unknown: ['Unknown', 'bg-slate-100 text-slate-600'] };
const inp = 'px-2 py-1.5 text-sm border border-slate-300 rounded w-full';
const err = (e, m) => toast.error(e?.response?.data?.detail || m);

const Section = ({ icon: Icon, title, done, children, defaultOpen = true, testId }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-[#E2E2DF] rounded-md bg-white" data-testid={testId}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-2 px-4 py-2.5 text-left" data-testid={`${testId}-toggle`}>
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}<Icon size={14} className="text-[#D35400]" />
        <span className="text-sm font-semibold text-slate-800 flex-1">{title}</span>
        {done && <CheckCircle2 size={14} className="text-emerald-600" />}
      </button>
      {open && <div className="px-4 pb-4">{children}</div>}
    </div>
  );
};

function HistoryPanel({ wb }) {
  const jobs = wb.facts.jobs;
  return (
    <div className="space-y-2" data-testid="wb-history">
      {wb.client_said && (
        <div className="p-2 rounded bg-sky-50 border border-sky-200 text-xs text-sky-900" data-testid="wb-client-said">
          <b>Client told us:</b> {Object.entries(wb.client_said).map(([k, v]) => `${k.replace('_', ' ')}: ${v}`).join(' · ')}
        </div>
      )}
      {jobs.length === 0 && <p className="text-xs text-slate-500">No jobs on file for this property.</p>}
      {jobs.map((j) => (
        <div key={j.id} className="text-xs border-b border-slate-100 pb-2" data-testid={`wb-job-${j.id}`}>
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-slate-500">{j.year || '—'}</span>
            <a href={`/appraisals/${j.id}`} target="_blank" rel="noreferrer" className="font-semibold text-slate-800 hover:underline">{j.label}</a>
            <span className={`px-1.5 py-0.5 rounded ${OUTCOME[j.outcome][1]}`}>{OUTCOME[j.outcome][0]}</span>
            <span className="text-slate-500">{wb.categories[j.category]}</span>
            {j.amount ? <span className="ml-auto font-mono text-slate-600">${Number(j.amount).toLocaleString()}</span> : null}
          </div>
          {j.status && <div className="text-slate-500 mt-0.5">{j.status}</div>}
          {j.notes && <div className="text-slate-600 mt-0.5 line-clamp-3" title={j.notes}>{j.notes}</div>}
        </div>
      ))}
    </div>
  );
}

function RoofBasics({ wb, onFacts }) {
  const { api } = useAuth();
  const r = wb.facts.roof;
  const [f, setF] = useState({ roof_key: wb.facts.roof_key || '', floor_m2: r.floor_m2 || '', roof_angle: r.roof_angle || '', roof_m2: r.roof_m2 || '', roof_age: r.roof_age || '', roof_stories: r.roof_stories || '', roof_colour: r.roof_colour || '' });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const calc = useMemo(() => {
    const fl = parseFloat(f.floor_m2), a = parseFloat(f.roof_angle);
    return fl > 0 ? Math.round((a > 0 && a < 80 ? fl / Math.cos((a * Math.PI) / 180) : fl) * 10) / 10 : 0;
  }, [f.floor_m2, f.roof_angle]);
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.put(`/reconnect/workbench/${wb.property_id}/roof`, f); onFacts(data); toast.success('Roof details saved'); }
    catch (e) { err(e, 'Could not save'); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3" data-testid="wb-roof">
      {r.roof_type && !wb.facts.roof_key && <p className="text-xs text-amber-800">On file: “{r.roof_type}” — pick the matching roof type below.</p>}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <label className="col-span-2 text-xs text-slate-600">Roof type *
          <select data-testid="wb-roof-key" value={f.roof_key} onChange={(e) => set('roof_key', e.target.value)} className={inp}>
            <option value="">Choose…</option>{Object.entries(wb.roof_keys).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select></label>
        <label className="text-xs text-slate-600">Floor m²<input data-testid="wb-floor-m2" type="number" value={f.floor_m2} onChange={(e) => set('floor_m2', e.target.value)} className={inp} /></label>
        <label className="text-xs text-slate-600">Roof angle °<input data-testid="wb-roof-angle" type="number" value={f.roof_angle} onChange={(e) => set('roof_angle', e.target.value)} className={inp} /></label>
        <label className="text-xs text-slate-600">Roof m² (typed or from pins)<input data-testid="wb-roof-m2" type="number" value={f.roof_m2} onChange={(e) => set('roof_m2', e.target.value)} className={inp} /></label>
        <label className="text-xs text-slate-600">Age / year<input data-testid="wb-roof-age" value={f.roof_age} onChange={(e) => set('roof_age', e.target.value)} className={inp} /></label>
        <label className="text-xs text-slate-600">Storeys<input data-testid="wb-roof-stories" type="number" value={f.roof_stories} onChange={(e) => set('roof_stories', e.target.value)} className={inp} /></label>
        <label className="text-xs text-slate-600">Colour<input data-testid="wb-roof-colour" value={f.roof_colour} onChange={(e) => set('roof_colour', e.target.value)} className={inp} /></label>
      </div>
      <div className="flex items-center gap-3 text-xs text-slate-600">
        <span data-testid="wb-m2-summary">{parseFloat(f.roof_m2) > 0 ? `Roof m² ${f.roof_m2} (typed)` : calc ? `Roof m² ≈ ${calc} (floor m² × angle)` : 'Enter roof m², or floor m² + angle'}</span>
        <button data-testid="wb-roof-save" onClick={save} disabled={busy} className="ml-auto px-3 py-1.5 text-xs rounded bg-slate-900 text-white hover:bg-slate-700 disabled:opacity-50">{busy ? 'Saving…' : 'Save roof details'}</button>
      </div>
    </div>
  );
}

function KnownWorks({ wb, onFacts }) {
  const { api } = useAuth();
  const [n, setN] = useState({ category: 'restoration', year: '', outcome: 'done', source: 'memory', note: '' });
  const cats = Object.entries(wb.categories).filter(([k]) => k !== 'visit');
  const add = async () => {
    try { const { data } = await api.post(`/reconnect/workbench/${wb.property_id}/works`, n); onFacts(data); setN({ ...n, year: '', note: '' }); toast.success('Work added'); }
    catch (e) { err(e, 'Could not add'); }
  };
  const del = async (id) => { try { onFacts((await api.delete(`/reconnect/workbench/${wb.property_id}/works/${id}`)).data); } catch (e) { err(e, 'Could not remove'); } };
  return (
    <div className="space-y-2" data-testid="wb-works">
      <p className="text-xs text-slate-500">Jobs in the CRM are listed under History. Add works you remember or found in your files — letters only mention work that's done.</p>
      {wb.facts.works.map((w) => (
        <div key={w.id} className="flex items-center gap-2 text-xs" data-testid={`wb-work-${w.id}`}>
          <span className="font-mono">{w.year}</span><b>{w.label}</b><span className={`px-1.5 rounded ${OUTCOME[w.outcome][1]}`}>{OUTCOME[w.outcome][0]}</span>
          <span className="text-slate-500">from {w.source}{w.note ? ` · ${w.note}` : ''}</span>
          <button onClick={() => del(w.id)} className="ml-auto text-slate-400 hover:text-rose-600" data-testid={`wb-work-delete-${w.id}`}><Trash2 size={12} /></button>
        </div>
      ))}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2 items-end">
        <select data-testid="wb-work-category" value={n.category} onChange={(e) => setN({ ...n, category: e.target.value })} className={`${inp} col-span-2`}>{cats.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
        <input data-testid="wb-work-year" type="number" placeholder="Year" value={n.year} onChange={(e) => setN({ ...n, year: e.target.value })} className={inp} />
        <select data-testid="wb-work-outcome" value={n.outcome} onChange={(e) => setN({ ...n, outcome: e.target.value })} className={inp}><option value="done">Done</option><option value="quoted">Quoted, not done</option></select>
        <select data-testid="wb-work-source" value={n.source} onChange={(e) => setN({ ...n, source: e.target.value })} className={inp}><option value="memory">I remember</option><option value="files">My files</option><option value="client">Client told us</option></select>
        <button data-testid="wb-work-add" onClick={add} className="inline-flex items-center justify-center gap-1 px-2 py-1.5 text-xs rounded border border-[#D35400] text-[#D35400] hover:bg-orange-50"><Plus size={12} />Add work</button>
        <input data-testid="wb-work-note" placeholder="Note (optional)" value={n.note} onChange={(e) => setN({ ...n, note: e.target.value })} className={`${inp} col-span-2 md:col-span-6`} />
      </div>
    </div>
  );
}

function ScreenshotAndPins({ wb, onReload, onFacts }) {
  const { api } = useAuth();
  const aid = wb.recon.appraisal_id;
  const [pins, setPins] = useState(null);
  const [pick, setPick] = useState([]);
  const start = async () => {
    try { await api.post(`/reconnect/recon/${wb.property_id}/appraisal`, { notes: wb.recon.notes || '', rating: wb.recon.rating || '' }); toast.success('Drive-by appraisal started'); onReload(); }
    catch (e) { err(e, 'Could not start'); }
  };
  const loadPins = async () => {
    try { const { data } = await api.post(`/reconnect/workbench/${wb.property_id}/pin-lengths`, { preview: true }); setPins(data.pins); setPick(Object.keys(data.pins)); }
    catch (e) { err(e, 'Could not read pins'); }
  };
  const usePins = async () => {
    try { onFacts((await api.post(`/reconnect/workbench/${wb.property_id}/pin-lengths`, { fields: pick })).data); toast.success('Roof details updated from pins'); setPins(null); onReload(); }
    catch (e) { err(e, 'Could not copy'); }
  };
  if (!aid) return (
    <div className="text-xs text-slate-600 space-y-2" data-testid="wb-shot-start">
      <p>Screenshot, template and pins sit on a drive-by appraisal (it also produces the free roof report).</p>
      <button data-testid="wb-start-appraisal" onClick={start} className="px-3 py-1.5 rounded bg-slate-900 text-white">Start drive-by appraisal</button>
    </div>
  );
  const cd = { property_id: wb.property_id, appraisal_id: aid };
  const noop = () => {};
  const cur = wb.facts.roof;
  return (
    <div className="space-y-4 wb-embed" data-testid="wb-shot">
      <style>{'.wb-embed [data-testid="wf-snapshot-next"],.wb-embed [data-testid="wf-snapshot-back"],.wb-embed [data-testid="wf-snapshot-cancel"],.wb-embed [data-testid="wf-annotate-next"],.wb-embed [data-testid="wf-annotate-back"],.wb-embed [data-testid="wf-annotate-cancel"],.wb-embed [data-testid="wf-annotate-skip"]{display:none!important}'}</style>
      <RoofSnapshotConfirm action={{ params: {} }} capturedData={cd} onNext={noop} />
      <AnnotateRoofDiagram action={{ params: { no_template_hint: 'Pick a roof template in the card above first (then reopen this section).' } }} capturedData={cd} onNext={noop} />
      <div className="border-t border-slate-100 pt-3">
        <button data-testid="wb-pins-load" onClick={loadPins} className="px-3 py-1.5 text-xs rounded border border-slate-300 hover:bg-slate-50">Use pin lengths in roof details…</button>
        {pins && (
          <div className="mt-2 text-xs space-y-1" data-testid="wb-pins-diff">
            {Object.keys(pins).length === 0 && <p className="text-slate-500">No pin lengths yet — drop Ridge / Hip / Valley / Barge (or Face for m²) pins first.</p>}
            {Object.entries(pins).map(([k, v]) => (
              <label key={k} className="flex items-center gap-2">
                <input type="checkbox" checked={pick.includes(k)} onChange={() => setPick((p) => (p.includes(k) ? p.filter((x) => x !== k) : [...p, k]))} data-testid={`wb-pin-pick-${k}`} />
                <span className="w-28">{k.replace('_', ' ')}</span><span className="text-slate-500">now {cur[k] || '—'}</span><span>→ <b>{v}</b></span>
              </label>
            ))}
            {Object.keys(pins).length > 0 && <button data-testid="wb-pins-apply" onClick={usePins} disabled={!pick.length} className="mt-1 px-3 py-1.5 rounded bg-[#D35400] text-white disabled:opacity-40">Copy ticked values</button>}
          </div>
        )}
      </div>
    </div>
  );
}

function LetterPreview({ wb }) {
  const t = { first_name: wb.client.name_first || 'there', address: wb.address, update_link: '[link to free roof report]', unsubscribe_link: '[unsubscribe]',
    maintenance_discount: '[campaign]', other_discount: '[campaign]', ...wb.facts.tokens };
  const fill = (s) => (s || '').replace(/\{(\w+)\}/g, (m, k) => (t[k] !== undefined ? t[k] : m));
  const p = wb.facts.preview || {};
  const general = !p.body;
  return (
    <div className="text-xs" data-testid="wb-letter-preview">
      <p className="mb-2"><span className="px-2 py-0.5 rounded bg-orange-100 text-orange-900 font-semibold" data-testid="wb-letter-version">{wb.versions[wb.facts.version]}</span></p>
      {general ? <p className="text-slate-500">Uses the campaign's own letter.</p> : (
        <div className="p-3 bg-slate-50 rounded border border-slate-200 whitespace-pre-wrap font-serif text-[13px] text-slate-800">
          <b>{fill(p.subject)}</b>{'\n\n'}{fill(p.body)}
        </div>
      )}
      {!wb.facts.tokens.restoration_estimate && <p className="mt-2 text-slate-500">No estimate in the letter yet — needs roof type + roof m².</p>}
    </div>
  );
}

export function ReconWorkbench({ propertyId, onClose, onStatus }) {
  const { api } = useAuth();
  const [wb, setWb] = useState(null);
  const [notes, setNotes] = useState('');
  const load = useCallback(async () => {
    try { const { data } = await api.get(`/reconnect/workbench/${propertyId}`); setWb(data); setNotes(data.recon.office_notes || ''); }
    catch (e) { err(e, 'Could not open the workbench'); }
  }, [api, propertyId]);
  useEffect(() => { load(); }, [load]);
  const onFacts = (facts) => setWb((w) => ({ ...w, facts }));
  const ready = async () => {
    try { const { data } = await api.post(`/reconnect/workbench/${propertyId}/ready`, { ready: true, office_notes: notes }); toast.success('Ready for its letter'); onStatus && onStatus(data.status); onClose(); }
    catch (e) { err(e, 'Not ready yet'); }
  };
  return createPortal(
    <div className="fixed inset-0 z-[100] bg-black/40 flex justify-end" data-testid="recon-workbench">
      <div className="w-full max-w-6xl h-full bg-[#F7F6F3] overflow-y-auto shadow-2xl">
        <div className="sticky top-0 z-10 bg-white border-b border-[#E2E2DF] px-5 py-3 flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-slate-900 truncate" data-testid="wb-address">{wb?.address || 'Loading…'}</p>
            <p className="text-xs text-slate-500">{wb ? `${wb.client.name_first} ${wb.client.name_last} · ${wb.client.email || 'no email'}` : ''}</p>
          </div>
          {wb && !wb.facts.ready && <span className="text-xs text-amber-800" data-testid="wb-missing">Still needed: {wb.facts.ready_missing.join(', ')}</span>}
          <button data-testid="wb-mark-ready" disabled={!wb?.facts.ready} onClick={ready} className="px-3 py-1.5 text-sm rounded bg-[#D35400] text-white hover:bg-[#A04000] disabled:opacity-40">Ready for letter</button>
          <button data-testid="wb-close" onClick={onClose} className="p-1.5 rounded hover:bg-slate-100"><X size={16} /></button>
        </div>
        {!wb ? <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-slate-400" /></div> : (
          <div className="p-5 grid lg:grid-cols-5 gap-4">
            <div className="lg:col-span-2 space-y-4">
              <Section icon={History} title="History" testId="wb-sec-history"><HistoryPanel wb={wb} /></Section>
              <Section icon={Mail} title="Letter preview" testId="wb-sec-letter"><LetterPreview wb={wb} /></Section>
            </div>
            <div className="lg:col-span-3 space-y-4">
              <Section icon={Ruler} title="Roof basics" done={wb.facts.ready} testId="wb-sec-roof"><RoofBasics wb={wb} onFacts={onFacts} /></Section>
              <Section icon={History} title="Known works" done={wb.facts.works.length > 0} testId="wb-sec-works"><KnownWorks wb={wb} onFacts={onFacts} /></Section>
              <Section icon={Camera} title="Screenshot, template & pins" done={wb.has_screenshot} defaultOpen={false} testId="wb-sec-shot"><ScreenshotAndPins wb={wb} onReload={load} onFacts={onFacts} /></Section>
              <label className="block text-xs text-slate-600">Office notes (internal, not sent)
                <textarea data-testid="wb-office-notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className={inp} /></label>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
